import React, { useState, useRef } from 'react';
import {
  Container, Card, Typography, TextField, FormControlLabel, Checkbox,
  Button, Box, Stack, Alert, AlertColor, AlertTitle, Divider,
  IconButton, Accordion, AccordionSummary, AccordionDetails,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import SignaturePad, { SignaturePadRef } from '../Components/SignaturePad.tsx';
import PublicApiService from '../Services/publicApiService.ts';

interface Visit {
  date: string;
  bodyPartPierced: string;
  jewelryDescription: string;
  complications: string;
  acknowledgment: boolean;
  nonRefundable: boolean;
  legalWaiver: boolean;
  piercerPrintedName: string;
}

const BodyPiercingRecord = () => {
  const initialFormState = {
    lastName: '',
    firstName: '',
    middleName: '',
    address: '',
    cityStateZip: '',
    phone: '',
    dateOfBirth: '',
    race: '',
    sex: '',
    physicianName: '',
    physicianAddress: '',
    physicianCityStateZip: '',
    physicianPhone: '',
    emergencyContactName: '',
    emergencyContactAddress: '',
    emergencyContactCityStateZip: '',
    emergencyContactPhone: '',
    allergies: '',
    bleedingDisorders: '',
  };

  const initialVisit: Visit = {
    date: '',
    bodyPartPierced: '',
    jewelryDescription: '',
    complications: '',
    acknowledgment: false,
    nonRefundable: false,
    legalWaiver: false,
    piercerPrintedName: '',
  };

  const [formData, setFormData] = useState(initialFormState);
  const [visits, setVisits] = useState<Visit[]>([{ ...initialVisit }]);
  const [phoneError, setPhoneError] = useState('');
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: '',
    severity: 'warning',
  });

  // Create refs for signatures - each visit has customer + piercer sig
  const customerSigRefs = useRef<(SignaturePadRef | null)[]>([]);
  const piercerSigRefs = useRef<(SignaturePadRef | null)[]>([]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let phoneNumber = e.target.value.replace(/\D/g, '');

    if (phoneNumber.startsWith('1')) {
      phoneNumber = phoneNumber.slice(1);
    }

    if (phoneNumber.length > 10) {
      phoneNumber = phoneNumber.slice(0, 10);
    }

    if (phoneNumber.length !== 10 && phoneNumber.length > 0) {
      setPhoneError('Phone number must be exactly 10 digits.');
    } else {
      setPhoneError('');
    }

    setFormData({ ...formData, phone: phoneNumber });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name !== 'phone') {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleVisitChange = (index: number, field: keyof Visit, value: string | boolean) => {
    const updatedVisits = [...visits];
    updatedVisits[index] = { ...updatedVisits[index], [field]: value };
    setVisits(updatedVisits);
  };

  const addVisit = () => {
    if (visits.length < 4) {
      setVisits([...visits, { ...initialVisit }]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (phoneError) {
      setAlert({ open: true, message: 'Please enter a valid 10-digit phone number.', severity: 'error' });
      return;
    }

    if (formData.phone.length !== 10) {
      setAlert({ open: true, message: 'Please enter a valid 10-digit phone number.', severity: 'error' });
      return;
    }

    // Validate at least the first visit is filled
    const firstVisit = visits[0];
    if (!firstVisit.date || !firstVisit.bodyPartPierced) {
      setAlert({ open: true, message: 'Please fill in the visit details (date and body part pierced).', severity: 'error' });
      return;
    }

    // Validate acknowledgment for all filled visits
    for (let i = 0; i < visits.length; i++) {
      if (visits[i].date && !visits[i].acknowledgment) {
        setAlert({ open: true, message: `Please acknowledge the information statement for Visit ${i + 1}.`, severity: 'error' });
        return;
      }
    }

    // Validate customer signature for all filled visits
    for (let i = 0; i < visits.length; i++) {
      if (visits[i].date && customerSigRefs.current[i]?.isEmpty()) {
        setAlert({ open: true, message: `Please provide customer signature for Visit ${i + 1}.`, severity: 'error' });
        return;
      }
    }

    try {
      setSubmitting(true);
      const apiService = new PublicApiService();

      // Build visits with signature data
      const visitsWithSignatures = visits
        .filter(v => v.date) // Only include visits that have been filled
        .map((visit, i) => ({
          ...visit,
          customerSignature: customerSigRefs.current[i]?.toDataURL() || '',
          piercerSignature: piercerSigRefs.current[i]?.isEmpty() ? '' : piercerSigRefs.current[i]?.toDataURL() || '',
        }));

      const submitData = {
        ...formData,
        formType: 'bodyPiercingRecord',
        visits: visitsWithSignatures,
      };

      await apiService.postConsentData(submitData);

      setFormData(initialFormState);
      setVisits([{ ...initialVisit }]);
      customerSigRefs.current.forEach(ref => ref?.clear());
      piercerSigRefs.current.forEach(ref => ref?.clear());
      setSuccessModalOpen(true);
    } catch (error) {
      setAlert({ open: true, message: 'Error submitting the form. Please try again.', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const visitLabels = ['First', 'Second', 'Third', 'Fourth'];

  return (
    <Container maxWidth="md" sx={{ mt: 5, mb: 5 }}>
      {alert.open && (
        <Stack sx={{ width: '100%', mb: 2 }} spacing={2}>
          <Alert
            severity={alert.severity}
            onClose={() => setAlert({ ...alert, open: false })}
          >
            <AlertTitle>{alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)}</AlertTitle>
            {alert.message}
          </Alert>
        </Stack>
      )}

      <Card sx={{ p: 4, boxShadow: 3 }}>
        <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
          BODY PIERCING CUSTOMER RECORD
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
          (Please PRINT all information IN INK) — Florida Department of Health Form
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          {/* Customer Information */}
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Customer Information</Typography>

          <Stack spacing={2} direction="row">
            <TextField fullWidth label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} required />
            <TextField fullWidth label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} required />
            <TextField label="Middle" name="middleName" value={formData.middleName} onChange={handleChange} sx={{ minWidth: 120 }} />
          </Stack>

          <TextField fullWidth label="Address" name="address" value={formData.address} onChange={handleChange} required sx={{ mt: 2 }} />

          <TextField fullWidth label="City, State, Zip" name="cityStateZip" value={formData.cityStateZip} onChange={handleChange} required sx={{ mt: 2 }} />

          <Stack spacing={2} direction="row" sx={{ mt: 2 }}>
            <TextField
              fullWidth label="Telephone Number" type="tel" name="phone"
              value={formData.phone ? `+1 ${formData.phone}` : '+1 '}
              onChange={handlePhoneChange} required
              error={Boolean(phoneError)} helperText={phoneError}
            />
            <TextField
              fullWidth label="Date of Birth" type="date" name="dateOfBirth"
              value={formData.dateOfBirth} onChange={handleChange} required
              InputLabelProps={{ shrink: true }}
            />
          </Stack>

          <Stack spacing={2} direction="row" sx={{ mt: 2 }}>
            <TextField fullWidth label="Race" name="race" value={formData.race} onChange={handleChange} />
            <TextField label="Sex" name="sex" value={formData.sex} onChange={handleChange} sx={{ minWidth: 120 }} />
          </Stack>

          {/* Physician Information */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Physician Information</Typography>

          <TextField fullWidth label="Physician Name" name="physicianName" value={formData.physicianName} onChange={handleChange} sx={{ mt: 1 }} />
          <TextField fullWidth label="Physician Address" name="physicianAddress" value={formData.physicianAddress} onChange={handleChange} sx={{ mt: 2 }} />
          <TextField fullWidth label="Physician City, State, Zip" name="physicianCityStateZip" value={formData.physicianCityStateZip} onChange={handleChange} sx={{ mt: 2 }} />
          <TextField fullWidth label="Physician Telephone Number" name="physicianPhone" value={formData.physicianPhone} onChange={handleChange} sx={{ mt: 2 }} />

          {/* Emergency Contact */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Emergency Contact</Typography>

          <TextField fullWidth label="Emergency Contact Name" name="emergencyContactName" value={formData.emergencyContactName} onChange={handleChange} required sx={{ mt: 1 }} />
          <TextField fullWidth label="Emergency Contact Address" name="emergencyContactAddress" value={formData.emergencyContactAddress} onChange={handleChange} sx={{ mt: 2 }} />
          <TextField fullWidth label="Emergency Contact City, State, Zip" name="emergencyContactCityStateZip" value={formData.emergencyContactCityStateZip} onChange={handleChange} sx={{ mt: 2 }} />
          <TextField fullWidth label="Emergency Contact Telephone Number" name="emergencyContactPhone" value={formData.emergencyContactPhone} onChange={handleChange} required sx={{ mt: 2 }} />

          {/* Medical */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Medical Information</Typography>

          <TextField
            fullWidth multiline rows={2}
            label="List any allergies (including allergies to medications and topical solutions)"
            name="allergies" value={formData.allergies} onChange={handleChange}
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth
            label="Do you have a history of bleeding disorders?"
            name="bleedingDisorders" value={formData.bleedingDisorders} onChange={handleChange}
            sx={{ mt: 2 }}
          />

          {/* Visit Records */}
          <Divider sx={{ my: 3 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Visit Records</Typography>
            {visits.length < 4 && (
              <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={addVisit}>
                Add Visit
              </Button>
            )}
          </Box>

          {visits.map((visit, index) => (
            <Accordion key={index} defaultExpanded={index === 0} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 'bold' }}>
                  Customer's {visitLabels[index]} Visit
                  {visit.date && ` — ${visit.date}`}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <Stack spacing={2} direction="row">
                    <TextField
                      label="Date" type="date" name="date"
                      value={visit.date}
                      onChange={(e) => handleVisitChange(index, 'date', e.target.value)}
                      required={index === 0}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 180 }}
                    />
                    <TextField
                      fullWidth label="Body Part Pierced"
                      value={visit.bodyPartPierced}
                      onChange={(e) => handleVisitChange(index, 'bodyPartPierced', e.target.value)}
                      required={index === 0}
                    />
                  </Stack>

                  <TextField
                    fullWidth label="Description of Jewelry Used"
                    value={visit.jewelryDescription}
                    onChange={(e) => handleVisitChange(index, 'jewelryDescription', e.target.value)}
                  />

                  <TextField
                    fullWidth label="Description of any Complications during Piercing Procedure"
                    value={visit.complications}
                    onChange={(e) => handleVisitChange(index, 'complications', e.target.value)}
                    multiline rows={2}
                  />

                  {/* Acknowledgment */}
                  <Card sx={{ p: 2, boxShadow: 1, backgroundColor: '#f9f9f9' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={visit.acknowledgment}
                          onChange={() => handleVisitChange(index, 'acknowledgment', !visit.acknowledgment)}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          Prior to my piercing, I received verbal and written information about the following and
                          discussed it with my piercer or the establishment operator: 1) A brief description of my
                          piercing procedure; 2) Any precautions for me to take before my piercing; 3) A description
                          of the risks and possible consequences of body piercing services; 4) Instructions for care
                          and restrictions following my piercing; and 5) Restrictions against piercing of minors.
                        </Typography>
                      }
                      sx={{ alignItems: 'flex-start', m: 0 }}
                    />
                  </Card>

                  <Card sx={{ p: 2, boxShadow: 1, backgroundColor: '#f9f9f9' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={visit.nonRefundable}
                          onChange={() => handleVisitChange(index, 'nonRefundable', !visit.nonRefundable)}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          I acknowledge and agree that all piercing services and session fees are non-refundable.
                          I understand that Jasel is not responsible for lost, broken, damaged, or loosened jewelry,
                          and I am responsible for checking my piercing jewelry regularly to ensure it remains secure
                          and properly fastened.
                        </Typography>
                      }
                      sx={{ alignItems: 'flex-start', m: 0 }}
                    />
                  </Card>

                  <Card sx={{ p: 2, boxShadow: 1, backgroundColor: '#f9f9f9' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={visit.legalWaiver}
                          onChange={() => handleVisitChange(index, 'legalWaiver', !visit.legalWaiver)}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          I agree not to bring any legal action or claim against Jasel Jewelry, its owners, employees,
                          contractors, agents, affiliated companies, or associated personnel for issues arising from or
                          related to my voluntary decision to receive tattoo and/or body piercing services at this
                          establishment, except to the extent such waiver or limitation is prohibited by applicable law.
                        </Typography>
                      }
                      sx={{ alignItems: 'flex-start', m: 0 }}
                    />
                  </Card>

                  {/* Customer Signature */}
                  <SignaturePad
                    ref={(ref) => { customerSigRefs.current[index] = ref; }}
                    label="Customer Signature"
                    height={120}
                  />

                  {/* Piercer Info */}
                  <TextField
                    fullWidth label="Printed Name of Piercer"
                    value={visit.piercerPrintedName}
                    onChange={(e) => handleVisitChange(index, 'piercerPrintedName', e.target.value)}
                  />

                  {/* Piercer Signature */}
                  <SignaturePad
                    ref={(ref) => { piercerSigRefs.current[index] = ref; }}
                    label="Piercer/Operator Signature"
                    height={120}
                  />
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}

          {/* Notes */}
          <Card sx={{ p: 2, mt: 2, backgroundColor: '#fff3e0' }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Note:</strong> The establishment must obtain a written notarized consent statement before
              piercing a minor. If the minor is under the age of 16, the minor also must be accompanied by a
              parent or legal guardian. All customer records must be kept for at least two (2) years.
            </Typography>
          </Card>

          {/* Submit */}
          <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 4 }} disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit Customer Record'}
          </Button>
        </Box>
      </Card>
      {/* Success Modal */}
      <Dialog
        open={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        aria-labelledby="success-dialog-title"
      >
        <DialogTitle id="success-dialog-title">✅ Form Submitted</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your body piercing customer record has been submitted successfully! Thank you.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuccessModalOpen(false)} variant="contained" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BodyPiercingRecord;
